import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { API } from 'api';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';

import { apiBasePath, apiName } from 'assets';
import { setKits } from 'store/kits';
import { getMainAccountId } from 'store/account';
import { Kit } from '@repo/types';

type KitsPage = { result: Kit[]; next_cursor?: string | null };

const fetchKitsPage = async (mainAccountId: string, cursor: string) => {
  const response: KitsPage = await API.get(
    apiName,
    `${apiBasePath}/kits?cursor=${encodeURIComponent(cursor)}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.HOME,
        domain: DOMAINS.KITS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        mainAccountId,
        cursor,
      },
    },
  );
  return response;
};

const fetchKits = async (mainAccountId: string) => {
  const kits: Kit[] = [];
  const visitedCursors = new Set<string>();
  let cursor = '';

  while (true) {
    const page = await fetchKitsPage(mainAccountId, cursor);
    kits.push(...(page?.result ?? []));

    if (!page?.next_cursor) return kits;

    // A set, not an ordering assert: kits.id sorts by MySQL's case-insensitive
    // collation, which JS comparison can't reproduce — `next <= cursor` would
    // throw on valid pages as soon as two ids differ in case.
    if (visitedCursors.has(page.next_cursor)) {
      throw new Error(`Kits pagination revisited cursor '${page.next_cursor}'`);
    }
    visitedCursors.add(page.next_cursor);

    cursor = page.next_cursor;
  }
};
export const getKitsQuery = (mainAccountId: string | null) => ({
  queryKey: ['kits', mainAccountId],
  queryFn: async () => mainAccountId && (await fetchKits(mainAccountId)),
});
const useKits = () => {
  const dispatch = useDispatch();
  const mainAccountId = useSelector(getMainAccountId);
  const kitsQuery = useQuery(getKitsQuery(mainAccountId));

  useEffect(() => {
    if (kitsQuery.isSuccess && kitsQuery.data) {
      dispatch(setKits(kitsQuery.data));
    }
  }, [kitsQuery.isSuccess, kitsQuery.data, dispatch]);

  return kitsQuery;
};

export default useKits;
